import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator as CalcIcon, Calculator, ArrowLeft } from "lucide-react";
import { Button } from "@/common/components/ui/button";
import { Label } from "@/common/components/ui/label";
import { Card, CardContent } from "@/common/components/ui/card";
import { CompactCalculatorRow } from "../CompactCalculatorRow";
import { calculateGST, formatCurrency } from "../../utils/calculations";
import ProductLayout from "@/common/components/ProductLayout";

export const GSTCalculator = () => {
  const navigate = useNavigate();
  const [gstAmount, setGstAmount] = useState(10000);
  const [gstRate, setGstRate] = useState(18);
  const [gstType, setGstType] = useState<"exclusive" | "inclusive">(
    "exclusive"
  );

  const gstResult = useMemo(
    () => calculateGST(gstAmount, gstRate, gstType),
    [gstAmount, gstRate, gstType]
  );

  return (
    <ProductLayout>
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/calculators")}
              className="mb-4 pl-0 hover:bg-transparent hover:text-primary"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Calculators
            </Button>
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-3 text-primary">
                <Calculator className="h-8 w-8" />
              </div>
              <h1 className="text-3xl font-bold text-secondary">
                GST Calculator
              </h1>
            </div>
          </div>

          <div className="mx-auto max-w-7xl">
            <div className="grid gap-10 lg:grid-cols-2">
              <Card className="border-border bg-card">
                <CardContent className="space-y-6 p-6 md:p-8">
                  <div className="mb-2 flex items-center gap-3">
                    <div className="rounded-xl bg-orange-100 p-3">
                      <CalcIcon className="h-6 w-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-secondary">
                        GST Calculator
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Inclusive & Exclusive Tax
                      </p>
                    </div>
                  </div>
                  <div className="mb-6 flex justify-center">
                    <div className="flex rounded-xl bg-slate-100 p-1">
                      <button
                        onClick={() => setGstType("exclusive")}
                        className={`rounded-lg px-6 py-2 text-sm font-bold transition-all ${gstType === "exclusive" ? "bg-white text-primary shadow-sm" : "text-slate-500"}`}
                      >
                        Exclusive
                      </button>
                      <button
                        onClick={() => setGstType("inclusive")}
                        className={`rounded-lg px-6 py-2 text-sm font-bold transition-all ${gstType === "inclusive" ? "bg-white text-primary shadow-sm" : "text-slate-500"}`}
                      >
                        Inclusive
                      </button>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <CompactCalculatorRow
                      label={
                        gstType === "exclusive" ? "Base Amount" : "Total Amount"
                      }
                      value={gstAmount}
                      onChange={setGstAmount}
                      min={100}
                      max={10000000}
                      step={100}
                      prefix="₹"
                      colorClass="text-primary"
                      hideSlider
                    />

                    <div className="space-y-4">
                      <Label className="ml-2 font-bold text-slate-500">
                        GST Rate (%)
                      </Label>
                      <div className="flex gap-2">
                        {[5, 12, 18, 28].map(rate => (
                          <button
                            key={rate}
                            onClick={() => setGstRate(rate)}
                            className={`flex-1 rounded-xl border py-3 font-bold transition-all ${gstRate === rate ? "border-orange-600 bg-orange-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-orange-200"}`}
                          >
                            {rate}%
                          </button>
                        ))}
                      </div>
                      <div className="mt-2 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3">
                        <span className="text-sm font-medium text-slate-500">
                          Custom Rate:
                        </span>
                        <input
                          type="number"
                          value={gstRate}
                          onChange={e => setGstRate(Number(e.target.value))}
                          className="w-full border-none bg-transparent font-bold text-primary focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-border bg-muted/20">
                <CardContent className="p-8">
                  <div className="mb-8 text-center">
                    <p className="mb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Total Amount
                    </p>
                    <h2 className="text-4xl font-black text-orange-600">
                      {formatCurrency(gstResult.total)}
                    </h2>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-100 py-4">
                      <span className="font-medium text-muted-foreground">
                        Net Amount
                      </span>
                      <span className="text-xl font-bold text-secondary">
                        {formatCurrency(gstResult.net)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-slate-100 py-4">
                      <span className="font-medium text-muted-foreground">
                        GST ({gstRate}%)
                      </span>
                      <span className="text-xl font-bold text-orange-500">
                        {formatCurrency(gstResult.gst)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </ProductLayout>
  );
};
